import React from 'react';
import PropTypes from 'prop-types';
import { Button, SparkIcon, SparkModalPortal, Spinner, Text } from '@spark';
import '@/features/datasets/components/DataCollection/GenerateDataWithAI/generateWithAI.css';

/**
 * 820px Spark modal shell shared by every "Generate data with AI" flow:
 * header (title + subtitle + close), scrollable body, and a footer with an
 * optional left Spark ghost "Back" plus Cancel / primary actions.
 */
const GenAiModalShell = ({
    open,
    onClose,
    title,
    subtitle,
    children,
    primaryLabel,
    onPrimary,
    primaryDisabled = false,
    primaryDisableWhenReadOnly = false,
    isSubmitting = false,
    showBack = false,
    onBack,
    readOnly = false,
    footerMeta,
    footerError = '',
}) => (
    <SparkModalPortal
        open={open}
        onClose={onClose}
        destroyOnClose
        size="slot"
        // Above the Spark modal portal default (1100), matching the other
        // dataset modals that replaced antd zIndex 2000 dialogs.
        style={{ zIndex: 2000 }}
    >
        <div className="gdai-modal">
            <div className="gdai-modal__header">
                <div>
                    <div className="gdai-modal__title fs-unmask">{title}</div>
                    {subtitle ? <div className="gdai-modal__subtitle fs-unmask">{subtitle}</div> : null}
                </div>
                <button type="button" className="gdai-modal__close" aria-label="Close" onClick={onClose}>
                    <SparkIcon slug="close" size="medium" />
                </button>
            </div>
            <div className="gdai-modal__body">{children}</div>
            <div
                className={`gdai-modal__footer${
                    showBack || (readOnly && footerMeta) ? ' gdai-modal__footer--with-back' : ''
                }`}
            >
                {showBack && !readOnly ? (
                    <Button
                        variant="ghost"
                        size="medium"
                        className="gdai-modal__back-link fs-unmask"
                        onClick={onBack}
                        disabled={isSubmitting}
                    >
                        Back
                    </Button>
                ) : null}
                {readOnly && footerMeta ? footerMeta : null}
                <div className="gdai-modal__footer-actions">
                    {!readOnly && footerError ? (
                        <Text
                            as="p"
                            variant="detail-regular"
                            color="inherit"
                            className="gdai-modal__footer-error fs-unmask"
                            role="alert"
                        >
                            {footerError}
                        </Text>
                    ) : null}
                    <div className="gdai-modal__footer-buttons">
                        {readOnly ? (
                            <Button variant="primary" size="medium" onClick={onClose}>
                                Close
                            </Button>
                        ) : (
                            <>
                                <Button variant="tertiary" size="medium" onClick={onClose} disabled={isSubmitting}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="primary"
                                    size="medium"
                                    onClick={onPrimary}
                                    disabled={primaryDisabled || isSubmitting}
                                    disableWhenReadOnly={primaryDisableWhenReadOnly}
                                    startIcon={isSubmitting ? <Spinner size="small" /> : undefined}
                                >
                                    {primaryLabel}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </SparkModalPortal>
);

GenAiModalShell.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    children: PropTypes.node,
    primaryLabel: PropTypes.string,
    onPrimary: PropTypes.func,
    primaryDisabled: PropTypes.bool,
    primaryDisableWhenReadOnly: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    showBack: PropTypes.bool,
    onBack: PropTypes.func,
    readOnly: PropTypes.bool,
    footerMeta: PropTypes.node,
    footerError: PropTypes.string,
};

export default GenAiModalShell;
